import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService, USER_ID_KEY } from './services/userService';
import { UserProfile } from './types/user';

type Mode = 'login' | 'register';

function UserLogin() {
  const navigate = useNavigate();
  const [existingUser, setExistingUser] = useState<UserProfile | null>(null);
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const storedId = localStorage.getItem(USER_ID_KEY);
    if (storedId) {
      userService
        .getUser(storedId)
        .then(setExistingUser)
        .catch(() => localStorage.removeItem(USER_ID_KEY))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const resetForm = () => {
    setUsername('');
    setPin('');
    setError(null);
  };

  const switchMode = (next: Mode) => {
    resetForm();
    setMode(next);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const user = await userService.login(username.trim(), pin);
      localStorage.setItem(USER_ID_KEY, user.id);
      navigate('/profile');
    } catch {
      setError('Forkert brugernavn eller PIN. Prøv igen.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== 4 || !/^\d+$/.test(pin)) {
      setError('PIN skal være præcis 4 cifre.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const user = await userService.createUser(username.trim(), pin);
      localStorage.setItem(USER_ID_KEY, user.id);
      navigate('/profile');
    } catch {
      setError('Kunne ikke oprette bruger. Brugernavnet er måske allerede taget.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="App">
        <header className="App-header">
          <p>Indlæser...</p>
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="content-container">
          <button className="close-button" onClick={() => navigate('/')}>
            &times;
          </button>

          {existingUser ? (
            /* ── Already logged in ── */
            <div className="user-card">
              <p className="user-card-label">Logget ind som</p>
              <h2 className="user-card-name">{existingUser.username}</h2>
              <p className="user-card-xp">{existingUser.totalXp} XP</p>
              <button className="quiz-link user-btn" onClick={() => navigate('/profile')}>
                Brugerprofil
              </button>
              <button
                className="quiz-link user-btn user-btn-secondary"
                onClick={() => {
                  localStorage.removeItem(USER_ID_KEY);
                  setExistingUser(null);
                }}
              >
                Skift bruger
              </button>
            </div>
          ) : (
            /* ── Login / Register ── */
            <>
              {/* Tab switcher */}
              <div className="auth-tabs">
                <button
                  className={`auth-tab${mode === 'login' ? ' auth-tab--active' : ''}`}
                  onClick={() => switchMode('login')}
                >
                  Log ind
                </button>
                <button
                  className={`auth-tab${mode === 'register' ? ' auth-tab--active' : ''}`}
                  onClick={() => switchMode('register')}
                >
                  Opret konto
                </button>
              </div>

              {mode === 'login' ? (
                <form onSubmit={handleLogin} className="user-form">
                  <h2>Log ind</h2>
                  <input
                    className="user-input"
                    type="text"
                    placeholder="Brugernavn"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    maxLength={50}
                    autoComplete="username"
                  />
                  <input
                    className="user-input"
                    type="password"
                    placeholder="4-cifret PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    maxLength={4}
                    inputMode="numeric"
                    required
                    autoComplete="current-password"
                  />
                  {error && <p className="user-error">{error}</p>}
                  <button type="submit" className="quiz-link user-btn" disabled={submitting}>
                    {submitting ? 'Logger ind...' : 'Log ind'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleRegister} className="user-form">
                  <h2>Opret konto</h2>
                  <input
                    className="user-input"
                    type="text"
                    placeholder="Brugernavn"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    maxLength={50}
                    autoComplete="off"
                  />
                  <input
                    className="user-input"
                    type="password"
                    placeholder="4-cifret PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    maxLength={4}
                    inputMode="numeric"
                    required
                    autoComplete="new-password"
                  />
                  {error && <p className="user-error">{error}</p>}
                  <button type="submit" className="quiz-link user-btn" disabled={submitting}>
                    {submitting ? 'Opretter...' : 'Opret konto'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </header>
    </div>
  );
}

export default UserLogin;
